import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { getGatewayConfig, addBinding, removeBinding, addChannelAccount, testChannelToken } from '../api/gateway';

/**
 * Panel for managing channel bindings
 */
function ChannelBindingsPanel({ className = '' }) {
    const [bindings, setBindings] = useState([]);
    const [channels, setChannels] = useState([]);
    const [agents, setAgents] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // Form states
    const [showBindingForm, setShowBindingForm] = useState(false);
    const [showChannelForm, setShowChannelForm] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [removeConfirmId, setRemoveConfirmId] = useState(null);

    // Binding form
    const [bindingAgentId, setBindingAgentId] = useState('');
    const [bindingChannelType, setBindingChannelType] = useState('');
    const [bindingAccountId, setBindingAccountId] = useState('');

    // Channel form
    const [channelType, setChannelType] = useState('');
    const [channelName, setChannelName] = useState('');
    const [channelToken, setChannelToken] = useState('');
    const [tokenTestResult, setTokenTestResult] = useState(null);

    const channelTypes = [
        { id: 'slack', label: 'Slack' },
        { id: 'discord', label: 'Discord' },
        { id: 'telegram', label: 'Telegram' },
        { id: 'teams', label: 'Microsoft Teams' }
    ];

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await getGatewayConfig();
            setBindings(result.data?.bindings || []);
            setChannels(result.data?.channels || []);
            setAgents(result.data?.agents || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleTestToken = async () => {
        if (!channelType || !channelToken) return;

        try {
            const result = await testChannelToken(channelType, channelToken);
            setTokenTestResult({
                valid: result.data?.valid,
                message: result.data?.message || (result.data?.valid ? 'Token is valid' : 'Invalid token')
            });
        } catch (err) {
            setTokenTestResult({ valid: false, message: err.message });
        }
    };

    const handleAddBinding = async (e) => {
        e.preventDefault();
        if (!bindingAgentId || !bindingChannelType || !bindingAccountId) {
            setError('All fields are required.');
            return;
        }

        setIsSubmitting(true);
        setError(null);
        try {
            await addBinding({
                agentId: bindingAgentId,
                channelType: bindingChannelType,
                accountId: bindingAccountId
            });
            setBindingAgentId('');
            setBindingChannelType('');
            setBindingAccountId('');
            setShowBindingForm(false);
            await fetchData();
        } catch (err) {
            setError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleAddChannel = async (e) => {
        e.preventDefault();
        if (!channelType || !channelName || !channelToken) {
            setError('All fields are required.');
            return;
        }

        setIsSubmitting(true);
        setError(null);
        try {
            await addChannelAccount({
                type: channelType,
                name: channelName,
                token: channelToken
            });
            setChannelType('');
            setChannelName('');
            setChannelToken('');
            setTokenTestResult(null);
            setShowChannelForm(false);
            await fetchData();
        } catch (err) {
            setError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleRemoveBinding = async (bindingId) => {
        setIsSubmitting(true);
        try {
            await removeBinding(bindingId);
            setRemoveConfirmId(null);
            await fetchData();
        } catch (err) {
            setError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const getChannelIcon = (type) => {
        switch (type?.toLowerCase()) {
            case 'slack':
                return '#';
            case 'discord':
                return 'D';
            case 'telegram':
                return 'T';
            case 'teams':
                return 'M';
            default:
                return '?';
        }
    };

    return (
        <div className={`space-y-6 ${className}`}>
            {/* Error */}
            {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-600">{error}</p>
                </div>
            )}

            {/* Channel Accounts */}
            <div className="bg-white rounded-lg border border-gray-200">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                    <div>
                        <h4 className="text-sm font-medium text-gray-900">Channel Accounts</h4>
                        <p className="text-xs text-gray-500 mt-1">Connected messaging platforms</p>
                    </div>
                    <button
                        onClick={() => setShowChannelForm(!showChannelForm)}
                        className="px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
                    >
                        {showChannelForm ? 'Cancel' : 'Add Channel'}
                    </button>
                </div>

                {/* Add Channel Form */}
                {showChannelForm && (
                    <div className="p-4 bg-gray-50 border-b border-gray-200">
                        <form onSubmit={handleAddChannel} className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label htmlFor="channel-type" className="block text-sm font-medium text-gray-700">
                                        Channel Type
                                    </label>
                                    <select
                                        id="channel-type"
                                        value={channelType}
                                        onChange={(e) => {
                                            setChannelType(e.target.value);
                                            setTokenTestResult(null);
                                        }}
                                        className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                        disabled={isSubmitting}
                                    >
                                        <option value="">Select type</option>
                                        {channelTypes.map(ct => (
                                            <option key={ct.id} value={ct.id}>{ct.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label htmlFor="channel-name" className="block text-sm font-medium text-gray-700">
                                        Display Name
                                    </label>
                                    <input
                                        id="channel-name"
                                        type="text"
                                        value={channelName}
                                        onChange={(e) => setChannelName(e.target.value)}
                                        placeholder="e.g., My Workspace"
                                        className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                        disabled={isSubmitting}
                                    />
                                </div>
                            </div>
                            <div>
                                <label htmlFor="channel-token" className="block text-sm font-medium text-gray-700">
                                    API Token
                                </label>
                                <div className="mt-1 flex gap-2">
                                    <input
                                        id="channel-token"
                                        type="password"
                                        value={channelToken}
                                        onChange={(e) => {
                                            setChannelToken(e.target.value);
                                            setTokenTestResult(null);
                                        }}
                                        placeholder="Enter API token"
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                                        disabled={isSubmitting}
                                    />
                                    <button
                                        type="button"
                                        onClick={handleTestToken}
                                        disabled={!channelType || !channelToken}
                                        className="px-3 py-2 text-sm font-medium text-indigo-600 border border-indigo-300 rounded-md hover:bg-indigo-50 disabled:opacity-50"
                                    >
                                        Test
                                    </button>
                                </div>
                                {tokenTestResult && (
                                    <p className={`mt-1 text-xs ${tokenTestResult.valid ? 'text-green-600' : 'text-red-600'}`}>
                                        {tokenTestResult.message}
                                    </p>
                                )}
                            </div>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="w-full px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
                            >
                                {isSubmitting ? 'Adding...' : 'Add Channel'}
                            </button>
                        </form>
                    </div>
                )}

                {/* Channels List */}
                <div className="divide-y divide-gray-200">
                    {isLoading && channels.length === 0 ? (
                        <div className="p-8 text-center">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mx-auto" />
                        </div>
                    ) : channels.length === 0 ? (
                        <div className="p-8 text-center text-gray-500 text-sm">
                            No channels configured
                        </div>
                    ) : (
                        channels.map(channel => (
                            <div key={channel.id} className="p-4 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="h-10 w-10 bg-gray-100 rounded-lg flex items-center justify-center font-bold text-gray-600">
                                        {getChannelIcon(channel.type)}
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-gray-900">{channel.name}</p>
                                        <p className="text-xs text-gray-500 capitalize">{channel.type}</p>
                                    </div>
                                </div>
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                    channel.status === 'connected' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                }`}>
                                    {channel.status || 'Unknown'}
                                </span>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Bindings */}
            <div className="bg-white rounded-lg border border-gray-200">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                    <div>
                        <h4 className="text-sm font-medium text-gray-900">Bindings</h4>
                        <p className="text-xs text-gray-500 mt-1">Agent to channel mappings</p>
                    </div>
                    <button
                        onClick={() => setShowBindingForm(!showBindingForm)}
                        className="px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
                    >
                        {showBindingForm ? 'Cancel' : 'Add Binding'}
                    </button>
                </div>

                {/* Add Binding Form */}
                {showBindingForm && (
                    <div className="p-4 bg-gray-50 border-b border-gray-200">
                        <form onSubmit={handleAddBinding} className="space-y-3">
                            <div>
                                <label htmlFor="binding-agent" className="block text-sm font-medium text-gray-700">
                                    Agent
                                </label>
                                <select
                                    id="binding-agent"
                                    value={bindingAgentId}
                                    onChange={(e) => setBindingAgentId(e.target.value)}
                                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                    disabled={isSubmitting}
                                >
                                    <option value="">Select agent</option>
                                    {agents.map(agent => (
                                        <option key={agent.id} value={agent.id}>{agent.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label htmlFor="binding-channel-type" className="block text-sm font-medium text-gray-700">
                                        Channel Type
                                    </label>
                                    <select
                                        id="binding-channel-type"
                                        value={bindingChannelType}
                                        onChange={(e) => setBindingChannelType(e.target.value)}
                                        className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                        disabled={isSubmitting}
                                    >
                                        <option value="">Select type</option>
                                        {channelTypes.map(ct => (
                                            <option key={ct.id} value={ct.id}>{ct.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label htmlFor="binding-account" className="block text-sm font-medium text-gray-700">
                                        Channel Account
                                    </label>
                                    <select
                                        id="binding-account"
                                        value={bindingAccountId}
                                        onChange={(e) => setBindingAccountId(e.target.value)}
                                        className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                        disabled={isSubmitting}
                                    >
                                        <option value="">Select account</option>
                                        {channels
                                            .filter(c => !bindingChannelType || c.type === bindingChannelType)
                                            .map(channel => (
                                                <option key={channel.id} value={channel.id}>{channel.name}</option>
                                            ))
                                        }
                                    </select>
                                </div>
                            </div>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="w-full px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
                            >
                                {isSubmitting ? 'Adding...' : 'Add Binding'}
                            </button>
                        </form>
                    </div>
                )}

                {/* Bindings List */}
                <div className="divide-y divide-gray-200">
                    {bindings.length === 0 ? (
                        <div className="p-8 text-center text-gray-500 text-sm">
                            No bindings configured
                        </div>
                    ) : (
                        bindings.map(binding => (
                            <div key={binding.id} className="p-4 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="text-sm">
                                        <span className="font-medium text-gray-900">{binding.agentName || binding.agentId}</span>
                                        <span className="text-gray-500 mx-2">&rarr;</span>
                                        <span className="text-gray-700">{binding.channelName || binding.accountId}</span>
                                        <span className="text-gray-400 text-xs ml-2">({binding.channelType})</span>
                                    </div>
                                </div>
                                {removeConfirmId === binding.id ? (
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => handleRemoveBinding(binding.id)}
                                            disabled={isSubmitting}
                                            className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50"
                                        >
                                            Confirm
                                        </button>
                                        <button
                                            onClick={() => setRemoveConfirmId(null)}
                                            className="text-sm text-gray-600 hover:text-gray-800"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                ) : (
                                    <button
                                        onClick={() => setRemoveConfirmId(binding.id)}
                                        className="text-sm text-red-600 hover:text-red-800"
                                    >
                                        Remove
                                    </button>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}

ChannelBindingsPanel.propTypes = {
    className: PropTypes.string
};

export default ChannelBindingsPanel;
